import * as httpStatus from "http-status";
import { CustomError } from "ts-custom-error";

import { DOMExceptionWithRequestUrl } from "../types";

export class ApiBadRequestError extends CustomError {
  public constructor(message?: string) {
    super(message);
    Object.defineProperty(this, "name", { value: "ApiBadRequestError" });
  }
}

export class ApiUnauthorizedError extends CustomError {
  public constructor(message?: string) {
    super(message);
    Object.defineProperty(this, "name", { value: "ApiUnauthorizedError" });
  }
}

export class ApiNotFoundError extends CustomError {
  public constructor(message?: string) {
    super(message);
    Object.defineProperty(this, "name", { value: "ApiNotFoundError" });
  }
}

export class ApiForbiddenError extends CustomError {
  public constructor(message?: string) {
    super(message);
    Object.defineProperty(this, "name", { value: "ApiForbiddenError" });
  }
}

export class ApiTimeoutError extends CustomError {
  public readonly requestUrl?: string;
  public readonly apiName?: string;
  public readonly abortReason?: string;
  public readonly abortSource?: "timeout" | "caller" | "unknown";

  public constructor(exception: DOMExceptionWithRequestUrl) {
    const baseMessage = `${exception.name} [${exception.code}] ${exception.message || ""}`.trim();
    const metadata: string[] = [];
    if (exception.requestUrl) metadata.push(`url: ${exception.requestUrl}`);
    if (exception.apiName) metadata.push(`api: ${exception.apiName}`);
    if (exception.abortSource) metadata.push(`abortSource: ${exception.abortSource}`);
    if (exception.abortReason) metadata.push(`abortReason: ${exception.abortReason}`);

    const detailMessage = metadata.length > 0 ? `${baseMessage} (${metadata.join(", ")})` : baseMessage;
    super(detailMessage);
    this.requestUrl = exception.requestUrl;
    this.apiName = exception.apiName;
    this.abortReason = exception.abortReason;
    this.abortSource = exception.abortSource;
    Object.defineProperty(this, "name", { value: "ApiTimeoutError" });
  }
}

export class ApiInternalServerError extends CustomError {
  public constructor(message?: string) {
    super(message);
    Object.defineProperty(this, "name", { value: "ApiInternalServerError" });
  }
}

export class ApiUnprocessableEntityError extends CustomError {
  public constructor(message?: string) {
    super(message);
    Object.defineProperty(this, "name", { value: "ApiUnprocessableEntityError" });
  }
}

export class ApiError extends CustomError {
  public constructor(message?: string) {
    super(message);
    Object.defineProperty(this, "name", { value: "ApiError" });
  }
}

export class ApiCustomError extends Error {
  public constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    Object.defineProperty(this, "name", { value: "ApiCustomError" });
  }
}

export const apiResponseStatusToError = (response: Response, detail: string) => {
  if (response.status === httpStatus.BAD_REQUEST) {
    return new ApiBadRequestError(detail);
  }

  if (response.status === httpStatus.UNAUTHORIZED) {
    return new ApiUnauthorizedError(detail);
  }

  if (response.status === httpStatus.FORBIDDEN) {
    return new ApiForbiddenError(detail);
  }

  if (response.status === httpStatus.NOT_FOUND) {
    return new ApiNotFoundError(detail);
  }

  if (response.status === httpStatus.INTERNAL_SERVER_ERROR) {
    return new ApiInternalServerError(detail);
  }

  if (response.status === httpStatus.UNPROCESSABLE_ENTITY) {
    return new ApiUnprocessableEntityError(detail);
  }

  // other response
  return new ApiError(detail);
};
